const steps = [
  {
    n: "01",
    t: "Map the system",
    d: "Before I touch a screen, I learn how the product actually works: who the roles are, where the data comes from, what the business rules are underneath. Most UX problems are structural.",
  },
  {
    n: "02",
    t: "Find where it breaks",
    d: "I run your real flows the way a new user would and write down every place they'd stall, hesitate, or quietly give up. Onboarding, permissions, empty states, error recovery — that's where products lose people.",
  },
  {
    n: "03",
    t: "Redesign the sequence",
    d: "Not a list of recommendations. Actual screens, in order, showing what the flow should be — including the paths that go wrong, which is the part most products never design.",
  },
  {
    n: "04",
    t: "Hand off with the reasoning",
    d: "Dev-ready files plus the why behind each decision, so your team can extend the thinking after I'm gone instead of guessing.",
  },
];

export default function HowIWork() {
  return (
    <section
      id="process"
      className="border-b border-[#0A0A0A] scroll-mt-16"
      data-testid="how-i-work-section"
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-24 md:py-32">
        <p className="label-mono text-[#4C1D95] mb-6">/ 03 — How I work</p>

        {/* Intro block */}
        <div className="max-w-3xl mb-16 md:mb-24">
          <p className="label-mono text-[#595959] mb-5">What you end up with</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter mb-8">
            A product people can get through
            <br className="hidden md:block" /> without being told how.
          </h2>
          <div className="space-y-5 text-lg md:text-xl leading-relaxed text-[#3F3F46]">
            <p>
              UX for founders whose product works but has grown faster
              than anyone had time to design for — where the demo lands and the
              second session doesn&apos;t.
            </p>
            <p>
              Every engagement runs on the same path — the{" "}
              <strong className="font-semibold text-[#0A0A0A]">
                Clarity Ladder
              </strong>
              . It starts with an honest read of where you actually stand, moves
              to fixing the flows that cost you the most, and ends, if it makes
              sense, with me embedded on your team as the product keeps changing.
            </p>
            <p>
              Based in Toronto, working remotely with teams anywhere. I optimize
              what already exists rather than starting from a blank canvas —
              which means shorter engagements, less rework, and decisions your
              team can defend after I&apos;m gone.
            </p>
          </div>
        </div>

        {/* Steps */}
        <h3 className="font-display text-3xl md:text-5xl tracking-tighter mb-10 md:mb-14">
          Same four steps, every time<span className="text-[#4C1D95]">.</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#0A0A0A]">
          {steps.map((s) => (
            <div
              key={s.n}
              className="border-b border-r border-[#0A0A0A] p-7 md:p-8 flex flex-col"
              data-testid={`how-step-${s.n}`}
            >
              <span className="font-display text-4xl md:text-5xl tracking-tighter text-[#4C1D95] mb-5">
                {s.n}
              </span>
              <h3 className="font-display text-2xl tracking-tighter mb-3 leading-tight">
                {s.t}
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-[#52525B]">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
